import styles from "../styles.module.scss";
import { useEffect, useRef, useState } from "react";
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  Typography,
  Stack,
  IconButton,
  CodeBlock,
} from "@altimateai/lego";
import { CodeSQLEditor, type SQLEditorRef } from "@/components/codeEditor";
import { QueryTextIndicesResponse } from "@/pages/query/types";
import { Link } from "react-router-dom";
import { IconChevronLeft, IconChevronRight, IconExternalLink } from "@tabler/icons-react";

type Index = [[number, number], [number, number]];

interface Props {
  query_response?: QueryTextIndicesResponse;
  query_hash: string;
  openAccordion: string;
  spans: string[];
}

export const QueryTextItem = ({ query_response, query_hash, openAccordion, spans }: Props) => {
  const queryText = query_response?.query_text || "";
  const editorRef = useRef<SQLEditorRef>(null);
  const [index, setIndex] = useState(0);

  // Process spans data
  const spanIndexMap: Record<string, number> = {};
  const indices: { index: Index }[] = [];

  for (const _span in query_response?.spans) {
    const spanIndex = query_response?.spans[_span];
    if (!spanIndex) continue;
    spanIndexMap[_span] = indices.length;
    indices.push({ index: spanIndex });
  }

  const span = spans[index];

  const handleSpanChange = () => {
    if (!editorRef.current) return;
    if (query_response?.spans?.[span]) {
      editorRef.current.highlightSpan(indices[spanIndexMap[span]]);
    } else {
      editorRef.current.highlightSpan({
        index: [
          [0, 0],
          [0, 0],
        ],
      });
    }
  };

  // Highlight the current span when dependencies change
  useEffect(() => {
    handleSpanChange();
  }, [query_response?.spans, openAccordion, index, span, indices, spanIndexMap]);

  const handleEditorLoaded = (): void => {
    setTimeout(() => {
      if (!editorRef.current) return;
      editorRef.current.setHighlight(indices);
      handleSpanChange();
    }, 100);
  };

  return (
    <AccordionItem value={query_hash} className="al-border-none al-py-0">
      <div className={styles.table_row}>
        {queryText ? (
          <div className="al-text-brand al-flex-1">
            {queryText.length > 100 ? `${queryText.slice(0, 100)}...` : queryText}
          </div>
        ) : (
          <div>Unable to find query</div>
        )}
        <Stack align="center">
          {queryText ? (
            <Button asChild variant={"ghost"} size="icon" title="Go to query">
              <Link to={`/query/groups/${query_hash}`} target="_blank">
                <IconExternalLink />
              </Link>
            </Button>
          ) : null}
          <AccordionTrigger />
        </Stack>
      </div>

      <AccordionContent className="al-p-2">
        {queryText ? (
          <Stack className="al-mb-2">
            <div className="spacer" />
            <Stack align="center">
              Showing
              <Typography variant="section-heading" className="al-mb-0">
                {index + 1}
              </Typography>
              out of
              <Typography variant="section-heading" className="al-mb-0">
                {spans.length}
              </Typography>
              <IconButton
                onClick={() => setIndex(prev => Math.max(prev - 1, 0))}
                title="Previous"
                className="al-h-8 al-w-8"
              >
                <IconChevronLeft />
              </IconButton>
              <IconButton
                onClick={() => setIndex(prev => Math.min(prev + 1, spans.length - 1))}
                title="Next"
                className="al-h-8 al-w-8"
              >
                <IconChevronRight />
              </IconButton>
            </Stack>
          </Stack>
        ) : null}
        <Typography className="al-mb-1">Code span</Typography>
        <CodeBlock
          key={span}
          code={span}
          language="sql"
          fileName=""
          showLineNumbers={true}
          theme="dark"
          editorTheme="tomorrow"
          titleActions={null}
        />
        {queryText ? (
          <>
            <Typography className="al-mb-1">Full query</Typography>
            <CodeSQLEditor
              value={queryText}
              height="28vh"
              ref={editorRef}
              lineNumbers
              onloaded={handleEditorLoaded}
            />
          </>
        ) : null}
        {!query_response?.spans?.[span] && (
          <Typography className="al-mb-0 al-bg-bg-red al-p-2 al-rounded-md al-mt-1">
            Unable to map code to query
          </Typography>
        )}
      </AccordionContent>
    </AccordionItem>
  );
};
